export const methodologyData = {
  pipelineStages: [
    {
      index: "01",
      title: "Season Ingestion",
      description: "League and season records are normalized into a stable historical fixture base suitable for comparative modeling.",
      focus: "Acquisition layer",
    },
    {
      index: "02",
      title: "Temporal State Construction",
      description: "Every match is represented from the standpoint of what was knowable before kickoff, preserving chronological discipline.",
      focus: "Pre-match representation layer",
    },
    {
      index: "03",
      title: "Target-Specific Training",
      description: "Each football market is handled as its own supervised learning problem rather than being collapsed into a single undifferentiated output.",
      focus: "Supervised estimation layer",
    },
    {
      index: "04",
      title: "Upcoming Match Inference",
      description: "Upcoming fixtures are passed through the same representational logic used in historical construction and then evaluated with the current predictive baseline.",
      focus: "Inference layer",
    },
    {
      index: "05",
      title: "Publication And Ranking",
      description: "Model outputs are translated into product-facing markets, confidence surfaces, and salience-oriented match ranking signals.",
      focus: "Publication and salience layer",
    },
  ],
  featureFamilies: [
    {
      name: "League and match context",
      description: "Competition identity and scheduling position define the structural environment in which the match takes place.",
      columns: "Competition context, season identity, temporal position",
    },
    {
      name: "Cumulative strength state",
      description: "Season-to-date production and concession summarize underlying team quality and directional dominance.",
      columns: "Cumulative points, scoring volume, concession volume, differential strength",
    },
    {
      name: "Standing geometry",
      description: "Relative rank and distance from the leading edge capture table pressure and competitive significance.",
      columns: "Positional rank, distance from the top, comparative table pressure",
    },
    {
      name: "Rate statistics",
      description: "Rate-based measures normalize accumulated production and concession across unequal points in the season.",
      columns: "Scoring rate, concession rate, normalized weekly output",
    },
    {
      name: "Recency-weighted momentum",
      description: "Recent performance is compressed into opponent-aware momentum summaries that reward relevance and recency.",
      columns: "Momentum summaries, attacking recency, defensive recency",
    },
    {
      name: "Nested historical traces",
      description: "Detailed historical traces remain useful for interpretive layers even when predictive layers rely on compact representations.",
      columns: "Structured historical traces for explanatory presentation",
    },
  ],
  trainingProtocol: [
    {
      stage: "Split",
      implementation: "Stratified holdout evaluation with deterministic partitioning",
      purpose: "Preserve class balance while keeping evaluation replicable.",
    },
    {
      stage: "Feature search",
      implementation: "Relevance-ranked subset search across compact-to-wide representation sizes",
      purpose: "Identify economical versus broad feature views without manual retuning for every target.",
    },
    {
      stage: "Estimator evaluation",
      implementation: "Gradient-boosted tree baseline across candidate representations",
      purpose: "Estimate nonlinear interactions while remaining operationally practical.",
    },
    {
      stage: "Selection rule",
      implementation: "Validation-utility maximization over candidate configurations",
      purpose: "Persist one preferred predictive configuration per market family.",
    },
    {
      stage: "Artifact serialization",
      implementation: "Persisted model package with schema and evaluation metadata",
      purpose: "Support reproducible inference without exposing the full internal stack on the public page.",
    },
  ],
  methodologicalAxes: [
    {
      name: "Chronological integrity",
      score: 0.97,
      note: "Pre-match discipline is treated as a near-foundational requirement.",
    },
    {
      name: "Confidence accountability",
      score: 0.94,
      note: "Errors made with stronger conviction are regarded as materially more serious.",
    },
    {
      name: "Interpretive clarity",
      score: 0.91,
      note: "Outputs are optimized for public legibility, not only internal utility.",
    },
    {
      name: "Market specificity",
      score: 0.88,
      note: "Target families are handled as distinct analytical objects.",
    },
    {
      name: "Editorial salience",
      score: 0.84,
      note: "Ranking logic reflects which fixtures deserve attention, not only prediction confidence.",
    },
    {
      name: "Operational compactness",
      score: 0.78,
      note: "Representations are kept disciplined enough for repeatable deployment.",
    },
  ],
  confidenceProfile: [
    { confidence: 0.5, correct: 0.0, incorrect: 0.0 },
    { confidence: 0.58, correct: 0.04, incorrect: -0.08 },
    { confidence: 0.66, correct: 0.09, incorrect: -0.19 },
    { confidence: 0.74, correct: 0.15, incorrect: -0.35 },
    { confidence: 0.82, correct: 0.22, incorrect: -0.58 },
    { confidence: 0.9, correct: 0.29, incorrect: -0.87 },
    { confidence: 0.96, correct: 0.33, incorrect: -1.11 },
  ],
  supportedTargets: [
    { name: "Result", type: "Multiclass", expansion: "Home Win / Away Win / Draw" },
    { name: "Both Teams To Score", type: "Binary", expansion: "BTTS Yes / BTTS No" },
    { name: "Over 0.5", type: "Binary", expansion: "Over 0.5 / Under 0.5" },
    { name: "Over 1.5", type: "Binary", expansion: "Over 1.5 / Under 1.5" },
    { name: "Over 2.5", type: "Binary", expansion: "Over 2.5 / Under 2.5" },
    { name: "Over 3.5", type: "Binary", expansion: "Over 3.5 / Under 3.5" },
    { name: "Over 4.5", type: "Binary", expansion: "Over 4.5 / Under 4.5" },
  ],
  artifactMetrics: [
    {
      target: "Result",
      accuracy: 0.5051,
      f1: 0.4415,
      jaccard: 0.3082,
      profile: "Structurally difficult multiclass task with moderate separability.",
    },
    {
      target: "Both Teams To Score",
      accuracy: 0.5453,
      f1: 0.5281,
      jaccard: 0.3659,
      profile: "Balanced but noisy market influenced by both attacking and defensive asymmetry.",
    },
    {
      target: "Over 0.5",
      accuracy: 0.9322,
      f1: 0.8994,
      jaccard: 0.8689,
      profile: "Highly separable target because the event boundary is comparatively permissive.",
    },
    {
      target: "Over 1.5",
      accuracy: 0.7633,
      f1: 0.6637,
      jaccard: 0.5838,
      profile: "Strong signal target with materially better class structure than higher thresholds.",
    },
    {
      target: "Over 2.5",
      accuracy: 0.5602,
      f1: 0.5561,
      jaccard: 0.3871,
      profile: "Core market with meaningful practical value but substantially more uncertainty than lower thresholds.",
    },
    {
      target: "Over 3.5",
      accuracy: 0.6986,
      f1: 0.5967,
      jaccard: 0.4979,
      profile: "Higher-threshold target with cleaner negative separation than the 2.5-goal market.",
    },
    {
      target: "Over 4.5",
      accuracy: 0.8517,
      f1: 0.785,
      jaccard: 0.7261,
      profile: "Sparse-event target with strong separability but lower direct product emphasis.",
    },
  ],
  publicationMappings: [
    { target: "Result", raw: "First Team", published: "Home Win" },
    { target: "Result", raw: "Second Team", published: "Away Win" },
    { target: "Result", raw: "Draw", published: "Draw" },
    { target: "Both Teams To Score", raw: "True / False", published: "BTTS Yes / BTTS No" },
    { target: "Over x.5", raw: "True / False", published: "Over x.5 / Under x.5" },
  ],
  priorityMarkets: [
    "BTTS Yes",
    "BTTS No",
    "Over 2.5",
    "Under 2.5",
    "Home Win",
    "Away Win",
    "Draw",
  ],
  marketCommunicationColumns: [
    "Interpretability",
    "Editorial value",
    "Probability usefulness",
    "Homepage fitness",
  ],
  marketCommunicationMatrix: [
    {
      market: "Result",
      values: [5, 5, 4, 5],
      note: "Primary public anchor because the category is immediately legible.",
    },
    {
      market: "BTTS",
      values: [5, 4, 4, 4],
      note: "High readability with practical value for comparative fixture browsing.",
    },
    {
      market: "Over 2.5",
      values: [4, 5, 4, 5],
      note: "Core totals market with strong headline and sorting utility.",
    },
    {
      market: "Over 1.5",
      values: [4, 2, 3, 2],
      note: "Useful analytically, but less distinctive in public display.",
    },
    {
      market: "Over 0.5",
      values: [4, 1, 2, 1],
      note: "Often highly predictable, yet too permissive to dominate presentation.",
    },
    {
      market: "Over 4.5",
      values: [3, 2, 2, 1],
      note: "Structurally informative but secondary in public emphasis.",
    },
  ],
  featuredWeights: [
    { name: "League significance", value: 40, tier: "Dominant", description: "Competition prestige provides the strongest prior for homepage salience." },
    { name: "Table strength", value: 24, tier: "High", description: "Matches involving stronger table positions are treated as more consequential." },
    { name: "Competitive balance", value: 12, tier: "Moderate", description: "Close contests are preferred over obviously lopsided pairings." },
    { name: "Points equilibrium", value: 8, tier: "Supporting", description: "Points distance refines standing-based closeness." },
    { name: "Title proximity", value: 8, tier: "Supporting", description: "Closeness to the leading edge increases contextual relevance." },
    { name: "Confidence moderation", value: 8, tier: "Supporting", description: "Model confidence lifts a match without dominating the ranking system." },
  ],
  leagueImportance: [
    { name: "English Premier League", weight: 1.0, tier: "Tier I", note: "Anchor competition for the highest baseline salience." },
    { name: "Brazilian Serie A", weight: 0.97, tier: "Tier I", note: "Strong global relevance and high competitive visibility." },
    { name: "Italian Serie A", weight: 0.96, tier: "Tier I", note: "Top-tier European benchmark in the salience schedule." },
    { name: "Spanish La Liga", weight: 0.96, tier: "Tier I", note: "Shares the highest continental salience band." },
    { name: "German Bundesliga", weight: 0.95, tier: "Tier I", note: "Very high baseline importance with strong homepage priority." },
    { name: "French Ligue 1", weight: 0.92, tier: "Tier II", note: "High importance, though slightly below the premier top band." },
    { name: "Portuguese Primeira Liga", weight: 0.82, tier: "Tier III", note: "Meaningful secondary exposure when fixture context is strong." },
    { name: "Turkish Super Lig", weight: 0.8, tier: "Tier III", note: "Competitive relevance with selective elevation via match context." },
  ],
  limitations: [
    {
      title: "Estimator diversity",
      description: "The training scaffold supports multiple families, but the current registry contains only LightGBM.",
    },
    {
      title: "Calibration",
      description: "Published probabilities are used directly without an explicit post-hoc calibration stage.",
    },
    {
      title: "Compressed history",
      description: "Recent history is summarized into factors, which is efficient but lossy compared with sequence models.",
    },
    {
      title: "Heuristic ranking layer",
      description: "Featured value is transparent and interpretable, but it is still hand-weighted rather than learned.",
    },
  ],
};
